import { LDateTime } from '@app/components/date/LDateTime';
import { pushTo } from '@app/core/routing';
import { adminAuditPaths } from '@app/modules/audit/routing/admin-audit-paths';
import { toDateTimeStr } from '@mi-front/core';
import { MrtTable, SearchInput } from '@mi-front/ui';
import { Edit } from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReportDefinitionEntity } from '../types';

interface Props {
  definitions: ReportDefinitionEntity[];
  reportType: string;
}

/**
 * Report-definition templates table with a name SearchInput (not ListView — chip nav owns the toolbar).
 */
export function ReportDefinitionList({ definitions, reportType: _reportType }: Props) {
  const { formatMessage, $t } = useIntl();
  const [nameFilter, setNameFilter] = useState('');

  const filteredDefinitions = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) {
      return definitions;
    }
    return definitions.filter((definition) => definition.name.toLowerCase().includes(q));
  }, [definitions, nameFilter]);

  return (
    <Box>
      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <SearchInput
          value={nameFilter}
          onSearch={setNameFilter}
          placeholder={$t({
            id: 'audit.reportDefinitionList.nameFilterPlaceholder',
            defaultMessage: 'Buscar por nombre',
          })}
        />
      </Box>
      <MrtTable<ReportDefinitionEntity>
        data={filteredDefinitions}
        navigateToOnClick={(id) => adminAuditPaths.reportDefinitionById(id)}
        columns={[
          {
            header: formatMessage({
              id: 'audit.reportDefinitionList.name',
              defaultMessage: 'Nombre',
            }),
            accessorKey: 'name',
            Cell: ({ row: { original } }) => (
              <Box display="flex" alignItems="center" gap={2}>
                <span>{original.name}</span>
                {original.isDefault && (
                  <Chip
                    label={
                      <FormattedMessage
                        id="audit.reportDefinitionList.defaultTemplate"
                        defaultMessage="Plantilla default"
                      />
                    }
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            ),
            size: 200,
          },
          {
            header: formatMessage({
              id: 'audit.reportDefinitionList.createdAt',
              defaultMessage: 'Creado',
            }),
            accessorKey: 'createdAt',
            Cell: ({ row: { original } }) => (
              <LDateTime value={toDateTimeStr(original.createdAt)} />
            ),
            size: 120,
            enableColumnFilter: false,
          },
          {
            header: formatMessage({
              id: 'audit.reportDefinitionList.actions',
              defaultMessage: 'Acciones',
            }),
            Cell: ({ row: { original } }) => (
              <Tooltip
                title={
                  <FormattedMessage
                    id="audit.reportDefinitionList.editTooltip"
                    defaultMessage="Editar"
                  />
                }
              >
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    pushTo(adminAuditPaths.reportDefinitionById(original.id));
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            ),
            size: 80,
            enableColumnFilter: false,
          },
        ]}
        enableGlobalFilter={false}
        enableFilters={false}
        memoizeColumns={false}
        enableHiding={false}
        enableColumnActions={false}
      />
    </Box>
  );
}
